import React from 'react';
import {
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  Divider
} from '@mui/material';
import { useTitle } from '@dyl/systems/TitleManager';
import EditIcon from '@mui/icons-material/Edit';
import getAllTemplates from '@dyl/apiClients/backendApi/getAllTemplates';
import getActiveTemplate from '@dyl/apiClients/backendApi/getActiveTemplate';
import saveActiveTemplate from '@dyl/apiClients/backendApi/saveActiveTemplate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import { useSnackbar } from 'notistack';
import useStyles from './styles';
import HistoryLog from './HistoryLog';
import { useAuth } from '@dyl/systems/UserManager';
import SaveTemplateResponseModal from './saveTemplateResponseModal';


const ArcAutomarkerTemplate = () => {
  useTitle('ARC AI Automarker template');
  const classes = useStyles();
  const { user } = useAuth();

  const [templates, setTemplates] = React.useState([]);
  const [activeTemplateName, setActiveTemplateName] = React.useState(null);
  const [selectedVersions, setSelectedVersions] = React.useState({});
  const [isChangeMode, setIsChangeMode] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [selectedVersion, setSelectedVersion] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [fullTemplateList, setFullTemplateList] = React.useState({});
  const [saveTrigger, setSaveTrigger] = React.useState(false);
  const [showSaveTemplateResponseModal, setShowSaveTemplateResponseModal] = React.useState(false);
  const [saveTemplateResponseError, setSaveTemplateResponseError] = React.useState(false);
  const username = user?.attributes?.email;



  React.useEffect(() => {
    const fetchActiveTemplate = async () => {
      try {
        const response = await getActiveTemplate('productive_writing');
        setActiveTemplateName(response.template_name || null);
        setSelectedVersions((prev) => ({
          ...prev,
          [response.template_name]: response.template_version
        }));
      } catch (error) {
        const status = error?.response?.status;

        if ([400, 404, 500].includes(status)) {
          enqueueSnackbar('Failed to load active template.', {
            variant: 'error'
          });
        }
      }
    };

    fetchActiveTemplate();
  }, [enqueueSnackbar, saveTrigger]);


  const handleTemplateChange = async () => {
    try {
      const response = await getAllTemplates();
      setTemplates(response.templates || []);
      setFullTemplateList(response.fullResponse || {});
      setIsChangeMode(true);
    } catch (error) {
      if (error?.response?.status === 500) {
        enqueueSnackbar(
          'Unable to load templates. Please try again later.',
          { variant: 'error' }
        );
      }
    }
  };
  const handleSaveActiveTemplate = async () => {
    try {
      await saveActiveTemplate('productive_writing', {
        template_name: selectedTemplate.templateName,
        template_version: selectedVersion,
        response_payload: fullTemplateList,
        user_name: username
      });
      setSaveTrigger(!saveTrigger);
      setShowSaveTemplateResponseModal(true);
      setSaveTemplateResponseError(false);
      setSelectedTemplate(null);
      setSelectedVersion('');
    } catch (error) {
      const status = error?.response?.status;

      if ([400, 404, 500].includes(status)) {
        setShowSaveTemplateResponseModal(true);
        setSaveTemplateResponseError(true);
      }
    }
  };

  return (
    <>
      <Box className={classes.container}>
        <Typography gutterBottom className={classes.titleh1}>
          ARC Automarker template
        </Typography>
        <Typography variant="body1" gutterBottom className={classes.body}>
          This automarker is used for the A2 Productive Writing task only. This page allows the user to change the ARC Automarker template if needed. Please only change the template if instructed to do so and refer to relevant work instructions. This is unlikely to happen very often.
        </Typography>
        <Typography gutterBottom className={classes.titleh1} style={{ marginBottom: 32 }}>
          Active template
        </Typography>
        <div style={{ display: 'flex', gap: 10 }}>
          <TextField
            value={activeTemplateName || ''}
            label="Name"
            InputLabelProps={{
              style: { fontSize: '16px', fontWeight: 400, fontFamily: 'Roboto' }
            }}
            variant="filled"
            size="small"
            sx={{ width: '400px', height: '57px' }}
            InputProps={{
              readOnly: true,
              style: { width: '400px', height: '57px', fontSize: '20px', fontWeight: 700, fontFamily: 'Roboto' }
            }}
          />
          <TextField
            value={
              selectedVersions[activeTemplateName] !== undefined
                ? `${selectedVersions[activeTemplateName]}`
                : ''
            }
            label="Version"
            InputLabelProps={{
              style: { fontSize: '16px', fontWeight: 400, fontFamily: 'Roboto' }
            }}
            variant="filled"
            size="small"
            sx={{ width: '170px', height: '57px' }}
            InputProps={{
              readOnly: true,
              style: { width: '170px', height: '57px', fontSize: '20px', fontWeight: 700, fontFamily: 'Roboto' }
            }}
          />
          <Button variant="contained" onClick={handleTemplateChange} disabled={isChangeMode} startIcon={<EditIcon />} sx={{ height: '48px', marginLeft: '194px', width: '144px', borderRadius: '26px', fontSize: '18px', fontWeight: 500, backgroundColor: 'rgba(113, 61, 144, 1)' }}>
            CHANGE
          </Button>
        </div>
        {isChangeMode && (
          <Box style={{ marginTop: 36 }}>
            <Box style={{ display: 'flex', gap: 285, alignItems: 'center', marginLeft: '11px' }}>
              <Typography style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'Roboto' }} gutterBottom>
                Select template
              </Typography>
              <Typography style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'Roboto' }} gutterBottom>
                Version
              </Typography>
            </Box>


            <Divider style={{ border: '1px solid rgba(185, 185, 185, 1)', width: '400px' }} />

            <Box style={{ display: 'flex', gap: 25 }}>
              {/* LEFT: Template list */}
              <Box style={{ width: 400 }}>
                {templates.map((template) => (
                  <Box
                    key={template.templateName}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setSelectedVersion('');
                    }}
                    style={{
                      padding: '6px 11px',
                      borderBottom: '1px solid rgba(185, 185, 185, 1)',
                      cursor: 'pointer',
                      height: '40px',
                      background:
                        selectedTemplate?.templateName === template.templateName
                          ? 'rgba(212, 211, 210, 1)'
                          : 'transparent',
                      fontWeight: 400,
                      fontSize: '18px',
                      fontFamily: 'Roboto'
                    }}
                  >
                    {template.templateName}
                  </Box>
                ))}
              </Box>

              {/* RIGHT: Version seect */}
              <Box>
                <Select
                  size="small"
                  value={selectedVersion}
                  disabled={!selectedTemplate}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  IconComponent={open ? ClearIcon : KeyboardArrowDownIcon}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: '#9e9e9e' }}>
                          Select
                        </span>
                      );
                    }
                    return selected;
                  }}
                  sx={{ width: 155, height: 40, }}
                  MenuProps={{ MenuListProps: { sx: { paddingBottom: 0, paddingTop: 0 }}}}
                >
                  {selectedTemplate?.versions.map((v) => (
                    <MenuItem key={v.version} value={v.version} sx={{ borderBottom: '1px solid rgba(185, 185, 185, 1)', paddingTop: '0px', paddingBottom: '0px', height: '40px', fontSize: '18px', fontFamily: 'Roboto' }}>
                      {v.version}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            {/* FOOTER ACTIONS */}
            <Box
              style={{
                display: 'flex',
                gap: 16,
                marginLeft: '650px',
                marginTop: '-45px',
              }}
            >
              <Button
                variant="text"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setIsChangeMode(false);
                  setSelectedTemplate(null);
                  setSelectedVersion('');
                }}
                sx={{ height: '48px', width: '130px', borderRadius: '26px', fontSize: '18px', fontWeight: 500, color: '#000' }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                onClick={handleSaveActiveTemplate}
                disabled={!selectedTemplate || !selectedVersion || showSaveTemplateResponseModal}
                sx={{ height: '48px', width: '130px', borderRadius: '26px', fontSize: '18px', fontWeight: 500, backgroundColor: 'rgba(113, 61, 144, 1)' }}
              >
                ACTIVATE
              </Button>
            </Box>
          </Box>
        )}

      </Box>
      <Divider style={{ marginTop: 56 }} />
      <HistoryLog isChangeMode={showSaveTemplateResponseModal} />
      <SaveTemplateResponseModal
        open={showSaveTemplateResponseModal}
        error={saveTemplateResponseError}
        onClose={() => {
          setShowSaveTemplateResponseModal(false);
          if (!saveTemplateResponseError) { setIsChangeMode(false); }
        }}
      />
    </>
  );
};

export default ArcAutomarkerTemplate;
